import { type ComputedRef, computed } from "vue";

import { flagVersion, getOpenFeatureClient } from "../featureflags";

/**
 * Feature flag keys. These MUST match the keys configured in GO Feature Flag
 * (and used by the website) so the same user gets the same results.
 */
export const FeatureFlag = {
  SupportChat: "support-chat",
} as const;

export type FeatureFlag = (typeof FeatureFlag)[keyof typeof FeatureFlag];

// addonFlag mirrors the backend featureflag.AddonFlag(itemID): the GOFF flag gating a Chargebee
// addon is "Addon-<Chargebee item id>" (case-sensitive, exact). TV-1774.
export const addonFlag = (chargebeeItemId: string): string => `Addon-${chargebeeItemId}`;

export function useFeatureFlag() {
  // Returns a ComputedRef so callers re-render when the OpenFeature cache
  // changes (initial fetch finishing, websocket pushes, context updates).
  // Reading the value once would freeze the first synchronous evaluation —
  // typically `false` before the provider is ready — and gated UI would stay
  // hidden even after flags load. Fails closed (false) when the client is unset.
  const hasAccessToFeature = (flag: FeatureFlag): ComputedRef<boolean> =>
    computed(() => {
      void flagVersion.value;
      const client = getOpenFeatureClient();
      if (!client) {
        return false;
      }
      return client.getBooleanValue(flag, false);
    });

  // isAddonAvailable gates non-list addon surfaces (the future in-app BackupBoost panel, deep
  // links) behind the Addon-<id> flag. The backend is the authoritative gate (TV-1774); this is
  // defense-in-depth. An ABSENT flag => available (default true), so un-flagged addons (e.g.
  // Modspace) are unaffected. Fails closed (false) when the client is unset — note this includes
  // the pre-ready window and any provider outage, during which EVERY addon (incl. un-flagged ones
  // that would otherwise default available) is hidden here. That is a deliberate fail-closed choice
  // for a defense-in-depth UI gate: the backend still decides what actually shows/sells, so a hidden
  // un-flagged surface degrades gracefully rather than exposing a gated one.
  const isAddonAvailable = (chargebeeItemId: string): ComputedRef<boolean> =>
    computed(() => {
      void flagVersion.value;
      const client = getOpenFeatureClient();
      if (!client) {
        return false;
      }
      return client.getBooleanValue(addonFlag(chargebeeItemId), true);
    });

  return { hasAccessToFeature, isAddonAvailable };
}
